// Copyright ©️ 2026 eVolve MEP, LLC

import './nav.css';

import type { ReactNode } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Box, Flex, Stack, Text, Menu, type MenuProps } from '@mantine/core';

import type { EvolveIconName } from 'assets/icons/EvolveIcon';
import { useIsMobile } from 'hooks/useIsMobile';
import { WithColorScheme } from 'styles/theme/ColorScheme';

import classes from './NavPopover.module.css';
import navSectionClasses from './NavSectionHeader.module.css';

const DELAY_MS = 150;

type Props = {
  target: ReactNode;
  icon?: EvolveIconName;
  label: ReactNode;
} & StrongOmit<MenuProps, 'shadow'>;

export const NavPopover = ({ target, label, children, ...props }: Props) => {
  const isMobile = useIsMobile();

  return (
    <Menu
      // On touch there's no hover, so `click-hover` never toggles closed — use plain `click` (toggles) on mobile.
      trigger={isMobile ? 'click' : 'click-hover'}
      openDelay={DELAY_MS}
      closeDelay={DELAY_MS}
      shadow="xl"
      {...props}
    >
      <Menu.Target>
        <Box w="100%">{target}</Box>
      </Menu.Target>
      <Menu.Dropdown p="md" className={classes.navDropdown} bg="app-shell-gray.9">
        <WithColorScheme colorScheme="dark">
          <Stack gap={0}>
            <Flex align="center" px="xs" h={30}>
              <Text fz="xs" className={navSectionClasses.text} fw={500}>
                {label}
              </Text>
            </Flex>
            {children}
          </Stack>
        </WithColorScheme>
      </Menu.Dropdown>
    </Menu>
  );
};
